import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

/* View Payslip's download: one month, or a run of up to MAX_MONTHS months of
   the same employee, as one PDF with a page per payslip. */

export const MAX_MONTHS = 6;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const field = (value) => (value && typeof value === "object" ? value.value : value);

// Months since year 0, so "the next month" is always +1 across a year end.
export const periodKey = (payslip) =>
  Number(payslip?.year) * 12 + Number(field(payslip?.month)) - 1;

export const periodLabel = (key) => `${MONTHS[key % 12]} ${Math.floor(key / 12)}`;

/**
 * The employee's payslips as one entry per month, newest first. If a month
 * somehow has two payslips, the newest record (highest id) wins.
 */
export const monthOptions = (payslips) => {
  const byKey = new Map();
  for (const payslip of payslips || []) {
    const key = periodKey(payslip);
    if (!Number.isFinite(key)) continue;
    const current = byKey.get(key);
    if (!current || Number(payslip.id) > Number(current.id)) byKey.set(key, payslip);
  }
  return [...byKey.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([key, payslip]) => ({ key, id: String(payslip.id), label: periodLabel(key) }));
};

/** The payslips from `fromKey` to `toKey` inclusive, oldest first. */
export const payslipsInRange = (options, fromKey, toKey) =>
  options
    .filter((option) => option.key >= fromKey && option.key <= toKey)
    .sort((a, b) => a.key - b.key);

export const pdfFilename = (name, fromKey, toKey) => {
  const span =
    fromKey === toKey
      ? periodLabel(fromKey)
      : Math.floor(fromKey / 12) === Math.floor(toKey / 12)
        ? `${MONTHS[fromKey % 12]}-${periodLabel(toKey)}`
        : `${periodLabel(fromKey)} - ${periodLabel(toKey)}`;
  return `${name ? name + " " : ""}payslip ${span}.pdf`;
};

/**
 * One portrait A4 page per element, each scaled to the page width (or height,
 * if taller) and pinned to the top. The elements must be laid out (in the
 * DOM, not display:none) when this runs.
 */
export const elementsToPdf = async (elements) => {
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 12;

  for (let i = 0; i < elements.length; i++) {
    const canvas = await html2canvas(elements[i], { scale: 2, backgroundColor: "#ffffff" });
    const ratio = Math.min(
      (pageWidth - margin * 2) / canvas.width,
      (pageHeight - margin * 2) / canvas.height
    );
    const width = canvas.width * ratio;
    const height = canvas.height * ratio;
    if (i > 0) pdf.addPage();
    pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", (pageWidth - width) / 2, margin, width, height);
  }

  return pdf;
};

export const saveElementsAsPdf = async (elements, filename) => {
  const pdf = await elementsToPdf(elements);
  pdf.save(filename);
};
